import { defineStore, storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'
import type { AxiosResponse } from 'axios'
import type { PasswordRule } from '@/composables/useValidatePassword'
import LOCAL_STORAGE_KEYS from '@constants/localStorageKeys'
import USD_COUNTRIES_ISO_CODES from '@constants/usdCurrencies'
import { client, uninterceptedClient } from '@/services/client'
import { handleRequest } from '@/services/handleRequest'
import { updateAuthToken } from '@/services/interceptors'
import { useSpace } from '@/store/modules/App/space'
import type { User, UserEnrichedInfoResponse, Token } from '@/types'
import type {
  GenerateLeadRequest,
  GenerateEnterpriseProspect,
} from '@/types/lead'
import { changeLanguageMessages, changeLocale } from '@utils/helpers/translate'
import { isEmpty } from '@/utils/checkers'
import { connectUnleashUser } from '@/utils/setup-application/createUnleash'
import type { SbTheme } from '@/components/ContentManager/AccountSettings/types'
import { usePartnerUser } from './../Partner/user'
import router from '@/router'

export type State = {
  user: User
  access_tokens: Token[]
  currency: 'usd' | 'eur'
  language: string
  languageLoaded: string
  inEu: boolean
  isUserLoggedIn: boolean
  loadingUserPromise: Promise<void> | null
  userEnrichedInfo: UserEnrichedInfoResponse
  themePreview: SbTheme | null
}

const state = (): State => ({
  user: {} as User,
  access_tokens: [],
  currency: 'usd',
  language: 'en',
  languageLoaded: 'en',
  inEu: true,
  isUserLoggedIn: false,
  loadingUserPromise: null,
  userEnrichedInfo: {} as UserEnrichedInfoResponse,
  themePreview: null,
})

export type UserStore = ReturnType<typeof useUser>

export const useUser = defineStore('user', {
  state,
  getters: {
    getUserId: (state: State): number => state.user.id,
    getUserInfo: (state: State): User => state.user || {},
    getUserTokens: (state: State): Token[] => state.access_tokens || [],
    getUserInEurope: (state: State): boolean => state.inEu,
    hasUser: (state: State): boolean => state.user && !isEmpty(state.user),
    getUserLanguage: (state: State): string => state.language,
    getUserCurrency: (state: State): 'usd' | 'eur' => state.currency,
    getUserNotified: (state: State): string[] => state.user.notified || [],
    getUserHasPartner: (state: State): boolean => state.user?.has_partner,
    hasUserNotifiedValue:
      (state: State) =>
      (value: string): boolean => {
        return state.user.notified && state.user.notified.indexOf(value) !== -1
      },
    hasUserDisclaimerIdFeatureValue:
      (state: State) =>
      (id: number): boolean =>
        state.user?.disclaimer_ids?.includes(id) ?? false,
    findUserNotifiedValues:
      (state: State) =>
      (value: string): string[] => {
        return state.user?.notified
          ? state.user.notified.filter((item) => item.includes(value))
          : []
      },
    getIsUserLoggedIn: (state: State): boolean => state.isUserLoggedIn,
    getUserTrackStatistics: (state: State): boolean =>
      state.user.track_statistics,
    getUserEnrichedInfo: (state: State): UserEnrichedInfoResponse =>
      state.userEnrichedInfo,
    getUserTheme: (state: State): SbTheme | undefined =>
      state.themePreview ?? state.user?.ui_theme?.theme,
  },
  actions: {
    setUserInfo(value: User): void {
      this.user = value
      this.language = value?.lang || 'en'
      changeLocale(this.language)
    },

    setUserLanguageLoaded(value: string): void {
      this.languageLoaded = value
    },

    setUserNotified(value: string[]): void {
      this.user.notified = value
    },

    setDisclaimerFeatureId(value: number): void {
      this.user.disclaimer_ids.push(value)
    },

    setAccessTokens(value: Token[]): void {
      this.access_tokens = value
    },

    clearUserInfo(): void {
      this.user = {}
      changeLocale('en')
    },

    setUserCurrency(value: 'usd' | 'eur'): void {
      this.currency = value
    },

    setUserRegion(value: boolean): void {
      this.inEu = value
    },

    setIsUserLoggedIn(value: boolean): void {
      this.isUserLoggedIn = value
    },

    setUserTrackStatistics(value: boolean): void {
      this.user.track_statistics = value
    },

    setUserEnrichedInfo(enrichedUser: UserEnrichedInfoResponse): void {
      this.userEnrichedInfo = enrichedUser
    },

    setUserThemePreview(theme: SbTheme | null = null): void {
      this.themePreview = theme
    },

    async attemptSignUp({
      payload,
      region,
    }: {
      payload: Record<string, unknown>
      region: string | null
    }): Promise<void> {
      const res = (await client.post('/users/signup', payload, {
        region,
      })) as AxiosResponse<{ access_token: string }>

      updateAuthToken(res.data.access_token)

      await this.loadUserInfo()
    },

    async attemptLogin({
      payload,
      region,
    }: {
      payload: {
        email: string
        password: string
        rid: string
        otp_attempt: string | null
        [key: string]: unknown
      }
      region: string | null
    }): Promise<User | undefined> {
      const res = (await client.post('/users/login', payload, {
        headers: {
          'x-requested-with': 'XMLHttpRequest',
        },
        region,
      })) as AxiosResponse<{ otp_required?: boolean; access_token: string }>

      const { data } = res

      updateAuthToken(data.access_token)

      if (data.otp_required) {
        return data
      }

      const response = await this.loadUserInfo()
      if (response && response.status === 'success' && response.data) {
        return response.data.user
      }
    },

    async getZendeskToken(): Promise<string> {
      try {
        const { data } = await client.get('/livechat_auth')
        return data.token
      } catch (error) {
        return Promise.reject(error)
      }
    },

    attemptLogout(): Promise<boolean> {
      return new Promise((resolve) => {
        this.clearUserInfo()
        localStorage.removeItem(
          LOCAL_STORAGE_KEYS.third_party_auth_route_to_redirect,
        )
        this.setIsUserLoggedIn(false)
        resolve(true)
      })
    },

    async resendConfirmationEmail(payload): Promise<void> {
      await client.post(`/users/resend_confirm`, payload)
    },

    async sendForgotPasswordEmail(payload): Promise<void> {
      await client.post(`/users/reset_password`, payload)
    },

    async setUserPassword(payload): Promise<void> {
      await client.post(`/users/set_password`, payload)
    },

    async confirmUserAccount(payload): Promise<void> {
      return await client.post(`/users/confirm`, payload)
    },

    async attemptSSOLogin(payload: unknown): Promise<User> {
      const res = (await client.post('/sso', payload)) as AxiosResponse<{
        access_token: string
      }>

      updateAuthToken(res.data.access_token)

      const response = await this.loadUserInfo()
      if (response && response.status === 'success' && response.data) {
        return response.data.user
      }
      throw new Error('SSO login failed')
    },

    async getSupportCenterToken(): Promise<string> {
      const { data } = await client.get('/helpcenter_auth')
      return data.token
    },

    async getCustomPasswordComplexity(token: string): Promise<PasswordRule> {
      const { data } = await client.get(
        `/users/${token}/custom_password_complexity`,
      )
      return data
    },

    async loadUserInfo() {
      if (this.loadingUserPromise) {
        return this.loadingUserPromise
      }

      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ user: User }>> =>
          client.get('users/me'),
      })

      if (response.status === 'success') {
        const { user } = response.data

        this.setUserInfo(user)

        this.setupUserHelpers(user)
        this.loadLanguageTranslation()

        if (user.partner_role && user.has_partner) {
          const { partner } = storeToRefs(usePartnerUser())
          const { loadPartnerInfo } = usePartnerUser()

          if (!partner.value) {
            loadPartnerInfo()
          }
        }
      } else {
        if (response.statusCode === 401) {
          router.push({ name: 'Logout' })
        }
      }

      return response
    },

    async loadUserAccessTokens(): Promise<Token[]> {
      const { data } = await client.get(`/tokens`)

      if (data?.tokens) {
        this.setAccessTokens(data.tokens)
      }

      return Promise.resolve(data?.tokens)
    },

    async loadLanguageTranslation(reload = false): Promise<void> {
      const language = this.user.lang || 'en'

      if (!reload && this.languageLoaded === language) {
        return
      }

      try {
        const res = await fetch(
          `https://lang.storyblok.com/v2/${language}.json`,
        )
        const jsonFile = await res.json()
        changeLanguageMessages(language, jsonFile)
        this.setUserLanguageLoaded(language)
      } catch (err) {
        console.error(err)
      }
    },

    async updateUserInfo(payload): Promise<User> {
      const requestPayload = payload ?? this.user
      const changedLanguage = requestPayload.lang !== this.language

      return await client
        .put('/users/me', requestPayload)
        .then(({ data }) => {
          const { user } = data
          if (user) {
            this.setUserInfo(user)
          }

          if (changedLanguage) {
            this.loadLanguageTranslation(true)
          }
          return user || data
        })
        .catch((err) => Promise.reject(err))
    },

    setupUserHelpers(user): void {
      Sentry.setUser({ id: user.id, email: user.email })
      connectUnleashUser(user)
    },

    async generateLead(payload: GenerateLeadRequest): Promise<void> {
      return await client.post('users/generate_lead?id=me', payload)
    },

    async generateEnterpriseProspect(
      payload: GenerateEnterpriseProspect,
    ): Promise<void> {
      return await client.post('users/generate_enterprise_prospect', payload)
    },

    async loadUserCurrency(): Promise<void> {
      const { data } = await client.get(`countries/me`)
      const isUsdCountry = USD_COUNTRIES_ISO_CODES.find(
        (isoCode) => isoCode === data.country,
      )

      if (!isUsdCountry) {
        this.setUserCurrency('eur')
      }

      this.setUserRegion(data.country !== 'US')
    },

    async addFeatureDisclaimerId(id: number | undefined): Promise<User | void> {
      if (id && !this.hasUserDisclaimerIdFeatureValue(id)) {
        return await this.updateUserInfo({
          user: {
            disclaimer_ids: this.user.disclaimer_ids.concat(id),
          },
        })
      }
    },

    async updateUserNotifiedValues(values = []): Promise<User | false> {
      if (values.length) {
        this.setUserNotified(values)
        return await this.updateUserInfo()
      }
      return false
    },

    async addUserNotifiedValue(value = ''): Promise<void> {
      if (value === '') {
        return
      }
      let currentValues = [...(this.user?.notified || [])]
      if (!currentValues.includes(value)) {
        currentValues = [...currentValues, value]
      }
      return await this.updateUserNotifiedValues(currentValues)
    },

    async removeUserNotifiedValue(value = ''): Promise<void> {
      if (value === '') {
        return
      }
      const currentValues = [...(this.user.notified || [])]
      const index = currentValues.indexOf(value)
      if (index !== -1) {
        currentValues.splice(index, 1)
      }
      return await this.updateUserNotifiedValues(currentValues)
    },

    toggleUserNotifiedValue(value = ' '): void {
      if (value === '' || !this.user?.notified) {
        return
      }

      if (!this.user.notified?.includes(value)) {
        this.addUserNotifiedValue(value)
      } else {
        this.removeUserNotifiedValue(value)
      }
    },

    async checkUserLoggedIn(): Promise<void> {
      try {
        const { data } = await uninterceptedClient.get('users/me')
        this.setIsUserLoggedIn(!!data?.user)
      } catch (error) {
        this.setIsUserLoggedIn(false)
      }
    },

    async requestApproval({ spaceId, approval }): Promise<void> {
      return await client.post(
        `spaces/${spaceId}/approvals/`,
        { approval },
        { region: useSpace().region },
      )
    },

    async changeUserTrackStatistics(
      payload: Partial<User>,
    ): Promise<AxiosResponse<{ user: User }>> {
      const response = await client.put('/users/me', payload)

      this.setUserTrackStatistics(response.data.user.track_statistics)

      return response
    },

    async removeAvatar(): Promise<void> {
      return await client.delete('/users/remove_avatar')
    },

    async loadUserEnrichedInfo(
      email: string,
    ): Promise<UserEnrichedInfoResponse> {
      const { data } = await client.get(`fetch_users_info?email=${email}`)
      this.setUserEnrichedInfo(data)
      return data
    },

    async editTokenName(token: Token): Promise<Token[]> {
      await client.put(`/tokens/${token.id}`, {
        name: token.name,
      })

      return this.loadUserAccessTokens()
    },

    async createUserToken(payload: object): Promise<Token> {
      const { data } = await client.post(`/tokens`, payload)

      return data
    },

    async deleteUserToken(tokenId: number): Promise<void> {
      await client.delete(`/tokens/${tokenId}`)
    },
  },
})
